import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useCurrentEvent } from "@/lib/event-context";
import { inboxUnreadCount } from "@/lib/api";
import { useIsStaff } from "@/lib/use-staff";
import { PushToggle } from "@/components/PushToggle";
import dayjs from "dayjs";

// Paden die de medewerker-code (afgeslankte weergave) mag zien.
const STAFF_PATHS = new Set(["/scan", "/tickets", "/ambassadeurs"]);

const NAV = [
  { section: "Overview", items: [
    { to: "/", label: "Dashboard", icon: "▣" },
    { to: "/orders", label: "Bestellingen", icon: "◆" },
    { to: "/orders/new", label: "+ Handmatig bestellen", icon: "✚" },
    { to: "/tickets", label: "Tickets & check-in", icon: "◌" },
    { to: "/ambassadeurs", label: "Ambassadeurs", icon: "🎖" },
  ]},
  { section: "Communicatie", items: [
    { to: "/inbox", label: "Inbox", icon: "▼", badgeKey: "inbox-unread" as const },
    { to: "/emails", label: "Verzonden mails", icon: "▲" },
    { to: "/mailings", label: "Mailings", icon: "✉" },
    { to: "/templates", label: "Templates", icon: "▥" },
    { to: "/invite", label: "Uitnodigen", icon: "★" },
  ]},
  { section: "Beheer", items: [
    { to: "/sponsors", label: "Sponsors", icon: "♛" },
    { to: "/cms/event", label: "Event­instellingen", icon: "⚙" },
  ]},
  { section: "Op locatie", items: [
    { to: "/scan", label: "QR-scanner", icon: "◉" },
  ]},
];

const PAGE_TITLES: Record<string, string> = {
  "/": "Dashboard",
  "/orders": "Bestellingen",
  "/orders/new": "Nieuwe bestelling",
  "/tickets": "Tickets & check-in",
  "/ambassadeurs": "Ambassadeurs",
  "/invite": "Uitnodigen & links",
  "/mailings": "Mailings",
  "/emails": "Verzonden mails",
  "/inbox": "Inbox",
  "/templates": "Templates",
  "/sponsors": "Sponsors",
  "/cms/event": "Event­instellingen",
};

export function Layout() {
  const { data: event } = useCurrentEvent();
  const staff = useIsStaff();
  const nav = staff
    ? NAV.map((sec) => ({ ...sec, items: sec.items.filter((it) => STAFF_PATHS.has(it.to)) }))
        .filter((sec) => sec.items.length > 0)
    : NAV;
  const { data: inboxUnread } = useQuery({
    queryKey: ["inbox-unread"],
    queryFn: inboxUnreadCount,
    refetchInterval: 60000,
    enabled: !staff,
  });
  const [user, setUser] = useState<{ email: string | null; initials: string } | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      const email = data.user?.email ?? null;
      const initials = email ? email.slice(0, 2).toUpperCase() : "??";
      setUser({ email, initials });
    });
  }, []);

  // Close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Lock body scroll while drawer open
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  const title = (() => {
    const exact = PAGE_TITLES[location.pathname];
    if (exact) return exact;
    if (location.pathname.startsWith("/orders/")) return "Bestelling";
    if (location.pathname.startsWith("/templates/")) return "Template";
    return "Admin";
  })();

  const eventCrumb = event
    ? `★ ${dayjs(event.starts_at).format("D MMM")} · ${dayjs(event.starts_at).diff(dayjs(), "day")} dagen`
    : "Geen event";

  return (
    <div className={`admin ${drawerOpen ? "drawer-open" : ""}`}>
      <button
        type="button"
        className="hamburger"
        aria-label="Menu"
        aria-expanded={drawerOpen}
        onClick={() => setDrawerOpen((v) => !v)}
      >
        <span /><span /><span />
      </button>

      <div
        className="drawer-backdrop"
        onClick={() => setDrawerOpen(false)}
        aria-hidden="true"
      />

      <aside className="sidebar">
        <div className="sb-brand">
          <img src="/logo.png" alt="" onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
          <div className="t">Haringparty<small>Admin · 2026</small></div>
          <button
            type="button"
            className="sb-close"
            aria-label="Sluit menu"
            onClick={() => setDrawerOpen(false)}
          >×</button>
        </div>

        {nav.map((sec) => (
          <div key={sec.section}>
            <div className="sb-section">★ {sec.section}</div>
            {sec.items.map((it) => {
              const badge = "badgeKey" in it && it.badgeKey === "inbox-unread" && inboxUnread ? inboxUnread : 0;
              return (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.to === "/"}
                  className={({ isActive }) => `sb-link ${isActive ? "active" : ""}`}
                >
                  <span className="ico">{it.icon}</span> <span className="lbl">{it.label}</span>
                  {badge > 0 && (
                    <span className="sb-badge">{badge}</span>
                  )}
                </NavLink>
              );
            })}
          </div>
        ))}

        <div className="sb-foot">
          <div className="me">
            <div className="av">{user?.initials ?? "??"}</div>
            <div className="me-info">
              <div className="me-email">{staff ? "Medewerker (deurcode)" : user?.email ?? "—"}</div>
              <div className="me-role">{staff ? "Scannen & lijsten" : "Beheerder"}</div>
            </div>
          </div>
          <button className="signout" onClick={() => supabase.auth.signOut()}>Uitloggen</button>
        </div>
      </aside>

      <main className="main">
        <div className="topbar">
          <div className="topbar-text">
            <div className="crumb">{eventCrumb}</div>
            <h1>{title}</h1>
          </div>
          <div className="topbar-actions">
            <PushToggle />
          </div>
        </div>
        <Outlet />
      </main>
    </div>
  );
}
